import { z } from 'zod';
import { DOMAIN_LIFECYCLE_STATES, DOMAIN_TLS_STATES, DOMAIN_VERIFICATION_STATES, domainKindSchema, } from './domains.js';
import { adminOrganizationSummarySchema } from './api.js';
import { productKeySchema } from './products.js';
import { isUnknownValue, knownOr } from './forward-compatible.js';
import { orgRoleSchema, portalRoleSchema, staffRoleSchema } from './roles.js';
/**
 * Milestone 2 contracts: modules and their effective state, entitlements, release flags, emergency stops,
 * membership administration, invitations, the custom domain lifecycle, support access and the audit log.
 * Design: docs/design/entitlements-usage-billing.md section 1. Every route below is a staff route
 * (admin audience, MFA, audited reads) unless its comment says otherwise.
 */
/* ------------------------------------------------------------------------------------------------ */
/* Modules                                                                                           */
/* ------------------------------------------------------------------------------------------------ */
export const MODULE_RELEASE_STATUSES = ['ga', 'beta', 'alpha', 'internal'];
/**
 * Intelligence levels, named as the live pricing page names them (Seasoned is the reference level). The
 * level is a module setting, part of every module's agent setting since 0.24.0 (before, only Enterprise quoting,
 * negotiation, booking and tracking carried it): it changes how many tokens an action uses, never the price of a
 * token.
 */
export const INTELLIGENCE_LEVELS = ['steady', 'seasoned', 'strategist', 'mastermind'];
export const intelligenceLevelSchema = z.enum(INTELLIGENCE_LEVELS);
export const DEFAULT_INTELLIGENCE_LEVEL = 'seasoned';
/**
 * 0.24.0: the agent setting every module carries in its configuration (Trace for Brokers foundations spec 5.5; plan
 * section 5, "Switches"): whether the module's agent works, the highest mode it may work in, and its intelligence level.
 * The level is the same intelligenceLevel the Enterprise modules carried before; it now belongs to every module.
 *
 * The modes are the shared agent runtime's interaction modes (trace-agent-runtime capability_matrix.MODES), narrowest
 * first: shadow reads, searches and drafts only; supervised asks a person before the steps its tools mark for notice;
 * autopilot acts within each tool's own safety rules; full_auto acts on everything a tool allows. A commitment always
 * needs a person's confirmation, in every mode. The highest mode is a ceiling: a product or person may choose a
 * narrower one, never a wider one.
 */
export const AGENT_MODES = ['shadow', 'supervised', 'autopilot', 'full_auto'];
export const agentModeSchema = z.enum(AGENT_MODES);
/** The highest mode of a module whose configuration never set one. */
export const DEFAULT_AGENT_HIGHEST_MODE = 'supervised';
/**
 * The configuration keys of the agent setting, the same in every module's configuration. They sit beside any other
 * setting a module has, so a configuration stored before 0.24.0 ({} or { intelligenceLevel }) stays valid: the missing
 * keys read as their defaults.
 */
export const MODULE_AGENT_CONFIG_KEYS = {
    enabled: 'agentEnabled',
    highestMode: 'agentHighestMode',
    intelligenceLevel: 'intelligenceLevel',
};
/**
 * The agent setting of one module as platform-api answers it to products: read from the module's configuration, with
 * the defaults of its catalog entry where the configuration sets nothing. A configured setting, not a state: the agent
 * works only while its module takes new work (enabled or connected), and every side effect still asks
 * POST /v1/internal/actions/authorize. In an answer, a mode this copy does not know reads as shadow, the narrowest.
 */
export const moduleAgentSettingSchema = z.object({
    enabled: z.boolean(),
    highestMode: knownOr(agentModeSchema, (raw) => isUnknownValue(raw, AGENT_MODES), 'shadow'),
    intelligenceLevel: intelligenceLevelSchema,
});
/** A side effect a module may take, as POST /v1/internal/actions/authorize names it. */
export const moduleActionSchema = z.string().regex(/^[a-z_]+$/);
/** A module of the catalog. The catalog is versioned code (seeded into the database), not free data. */
export const moduleDefinitionSchema = z.object({
    key: z.string().regex(/^[a-z]+\.[a-z_]+$/, 'product.module, for example enterprise.negotiation'),
    productKey: productKeySchema,
    name: z.string().min(2).max(80),
    description: z.string().max(400),
    /** Modules that must be enabled first; enforced on entitlement and on enable. */
    dependsOn: z.array(z.string()).default([]),
    releaseStatus: z.enum(MODULE_RELEASE_STATUSES),
    /** Connector kinds the module needs to be connected (operational prerequisites). */
    requiresConnectors: z.array(z.string()).default([]),
    /**
     * 0.7.0: the side effects the module may take. The action authorization refuses any action its module does
     * not declare, so a misspelled action is refused instead of passing.
     */
    actions: z.array(moduleActionSchema).default([]),
    /** 0.7.0: JSON Schema (draft 2020-12) of the module's organization configuration; no properties when it has none. */
    configSchema: z.record(z.string(), z.unknown()).optional(),
});
/** 0.7.0: GET the module catalog: the catalog's version (code) and every module of it. */
export const moduleCatalogSchema = z.object({
    version: z.string(),
    modules: z.array(moduleDefinitionSchema),
});
/** Effective state of one module for one organization (one pure function computes it; see the design doc). */
export const MODULE_STATES = [
    'not_entitled',
    'unreleased',
    'available',
    'enabled',
    'connected',
    'degraded',
    'disabled',
    'emergency_stopped',
    'spend_capped',
    // 0.9.0 (milestone 3): the subscription of the product's contract no longer collects (canceled, unpaid,
    // incomplete_expired at the provider), so no new work starts until billing is restored.
    'billing_inactive',
];
export const moduleStateSchema = z.enum(MODULE_STATES);
/** One machine-readable reason, with the layer it comes from, so the UI can explain the state. */
export const MODULE_REASON_LAYERS = [
    'entitlement',
    'release',
    'configuration',
    'dependency',
    'prerequisite',
    'emergency_stop',
    'spend_cap',
    // 0.9.0: the billing provider's subscription of the contract (billing_inactive).
    'billing',
    // 0.7.0: the user permission layer, in action authorization only (the actor, its membership, the action).
    'permission',
];
export const moduleReasonSchema = z.object({
    layer: z.enum(MODULE_REASON_LAYERS),
    code: z.string(),
    message: z.string(),
    /** Who caused it and when, when a person did (for example who disabled the module). */
    actor: z.object({ id: z.string().nullable(), displayName: z.string().nullable() }).optional(),
    at: z.iso.datetime().optional(),
});
export const ENTITLEMENT_SOURCES = ['plan', 'contract', 'trial', 'support_grant'];
export const entitlementSourceSchema = z.enum(ENTITLEMENT_SOURCES);
export const effectiveModuleSchema = z.object({
    module: moduleDefinitionSchema,
    state: moduleStateSchema,
    reasons: z.array(moduleReasonSchema),
    /**
     * The organization configuration (validated against the module's own schema) and its version. 0.24.0: enabled is
     * null when nobody has turned the module on or off yet (a configuration that only sets, for example, its agent
     * setting); the module is then available.
     */
    configuration: z
        .object({
        enabled: z.boolean().nullable(),
        config: z.record(z.string(), z.unknown()),
        version: z.number().int().nonnegative(),
    })
        .nullable(),
    entitlement: z
        .object({
        source: entitlementSourceSchema,
        startsAt: z.iso.datetime(),
        endsAt: z.iso.datetime().nullable(),
    })
        .nullable(),
});
/**
 * 0.7.0: the answer to a configuration change: the module as it is now, and every other module whose state
 * the change moved (disabling a module disables the modules that depend on it, with a recorded reason).
 */
export const moduleConfigurationResultSchema = effectiveModuleSchema.extend({
    affected: z.array(effectiveModuleSchema),
});
/** GET organization modules. */
export const organizationModulesSchema = z.object({
    organizationId: z.uuid(),
    modules: z.array(effectiveModuleSchema),
});
/**
 * PATCH a module's organization configuration. Optimistic concurrency through expectedVersion. 0.24.0: enabled may be
 * left out, which leaves the module on or off as it is (to change only its configuration, such as its agent setting);
 * a client that still sends it is read as before.
 */
export const moduleConfigurationChangeSchema = z.object({
    enabled: z.boolean().optional(),
    config: z.record(z.string(), z.unknown()).default({}),
    expectedVersion: z.number().int().nonnegative(),
    reason: z.string().min(3).max(500),
});
/** Emergency stop: no new work and no further side effects, immediately; released explicitly. */
export const emergencyStopRequestSchema = z.object({
    scope: z.enum(['organization', 'module']),
    moduleKey: z.string().optional(),
    reason: z.string().min(3).max(500),
});
export const emergencyStopSchema = z.object({
    id: z.uuid(),
    organizationId: z.uuid(),
    scope: z.enum(['organization', 'module']),
    moduleKey: z.string().nullable(),
    reason: z.string(),
    stoppedBy: z.string(),
    stoppedAt: z.iso.datetime(),
    releasedBy: z.string().nullable(),
    releasedAt: z.iso.datetime().nullable(),
});
/** 0.7.0: GET emergency stops of an organization, active and released, newest first. */
export const emergencyStopListSchema = z.object({ items: z.array(emergencyStopSchema) });
/** Entitlement grants beyond the plan: trials and support grants are time limited. */
export const entitlementGrantRequestSchema = z.object({
    moduleKey: z.string(),
    source: z.enum(['trial', 'support_grant', 'contract']),
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime().nullable(),
    reason: z.string().min(3).max(500),
});
/**
 * 0.7.0: one entitlement of an organization (GET entitlements, and the answer to a grant). status is computed
 * when it is read: active (in force now), scheduled (starts later) or ended. grantedBy is the display name of
 * the person who granted it, null for one the system wrote (the seed, a plan).
 */
export const entitlementSchema = z.object({
    id: z.uuid(),
    organizationId: z.uuid(),
    moduleKey: z.string(),
    source: entitlementSourceSchema,
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime().nullable(),
    grantedBy: z.string().nullable(),
    reason: z.string().nullable(),
    createdAt: z.iso.datetime(),
    status: z.enum(['active', 'scheduled', 'ended']),
});
export const entitlementListSchema = z.object({ items: z.array(entitlementSchema) });
const releaseRolloutSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('all') }),
    z.object({ kind: z.literal('organizations'), organizationIds: z.array(z.uuid()).max(1000) }),
    z.object({ kind: z.literal('off') }),
]);
const releaseEnvironmentSchema = z.enum(['development', 'test', 'staging', 'production']);
/** Release flags per environment: all organizations, a list, or off. */
export const releaseFlagSchema = z.object({
    moduleKey: z.string(),
    environment: releaseEnvironmentSchema,
    rollout: releaseRolloutSchema,
    updatedBy: z.string(),
    updatedAt: z.iso.datetime(),
});
/**
 * 0.7.0: PUT a release flag. Each environment has its own database, so platform-api sets flags of its own
 * environment only. Without a flag a module is released where its catalog status is ga, and nowhere else.
 */
export const releaseFlagChangeSchema = z.object({
    moduleKey: z.string(),
    environment: releaseEnvironmentSchema,
    rollout: releaseRolloutSchema,
    reason: z.string().min(3).max(500),
});
export const releaseFlagListSchema = z.object({ items: z.array(releaseFlagSchema) });
/** 0.7.0: the body of a revocation or a release (invitations, emergency stops, support access): an optional reason. */
export const reasonBodySchema = z.object({ reason: z.string().min(3).max(500).optional() });
/* ------------------------------------------------------------------------------------------------ */
/* Members and invitations                                                                           */
/* ------------------------------------------------------------------------------------------------ */
export const memberSchema = z.object({
    membershipId: z.uuid(),
    userId: z.uuid(),
    displayName: z.string(),
    email: z.string(),
    roles: z.array(orgRoleSchema),
    status: z.enum(['invited', 'active', 'suspended', 'removed']),
    products: z.array(z.object({ productKey: productKeySchema, productRole: z.string().nullable() })),
    identities: z.array(z.object({ provider: z.string(), connection: z.string().nullable() })),
    lastSeenAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
});
/** GET members: keyset pagination (cursor, limit at most 100), search by name or email, status filter. */
export const memberListQuerySchema = z.object({
    query: z.string().max(200).optional(),
    status: z.enum(['invited', 'active', 'suspended', 'removed']).optional(),
    productKey: productKeySchema.optional(),
    cursor: z.string().max(500).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
});
export const memberListSchema = z.object({
    items: z.array(memberSchema),
    nextCursor: z.string().nullable(),
    total: z.number().int().nonnegative(),
});
/** PATCH a membership. Suspending or removing revokes sessions (session_revocations) in the same transaction. */
export const membershipChangeSchema = z.object({
    roles: z.array(orgRoleSchema).min(1).optional(),
    status: z.enum(['active', 'suspended', 'removed']).optional(),
    products: z
        .array(z.object({ productKey: productKeySchema, productRole: z.string().nullable() }))
        .optional(),
    reason: z.string().min(3).max(500),
});
export const invitationCreateSchema = z.object({
    email: z.email().max(320),
    roles: z.array(orgRoleSchema).min(1),
    products: z.array(z.object({ productKey: productKeySchema, productRole: z.string().nullable() })),
    expiresInDays: z.number().int().min(1).max(30).default(7),
});
/**
 * POST /v1/admin/organizations/{id}/portals/{portalId}/invitations: invites a person of one of the organization's
 * customer accounts to that portal. Accepting it makes them a member of the portal for that account, with the role.
 */
export const portalInvitationCreateSchema = z.object({
    customerAccountId: z.uuid(),
    email: z.email().max(320),
    role: portalRoleSchema.default('portal_user'),
    expiresInDays: z.number().int().min(1).max(30).default(7),
});
export const invitationSummarySchema = z.object({
    id: z.uuid(),
    emailHint: z.string(),
    status: z.enum(['pending', 'accepted', 'expired', 'revoked']),
    createdAt: z.iso.datetime(),
    expiresAt: z.iso.datetime(),
    /** Development only: the link the development delivery adapter recorded. Absent in hosted environments. */
    developmentLink: z.string().optional(),
    /** 0.7.0: the organization roles the invitation grants. */
    roles: z.array(orgRoleSchema).optional(),
    /** 0.7.0: the products the invitation grants. */
    productKeys: z.array(productKeySchema).optional(),
});
/** 0.7.0: GET invitations of an organization, newest first. */
export const invitationListSchema = z.object({ items: z.array(invitationSummarySchema) });
/* ------------------------------------------------------------------------------------------------ */
/* Custom domains                                                                                    */
/* ------------------------------------------------------------------------------------------------ */
export const domainCreateSchema = z.object({
    hostname: z.string().min(4).max(253),
    kind: z.enum(['employee-app', 'customer-portal']),
    applicationKey: productKeySchema,
    portalId: z.uuid().optional(),
});
/** The DNS record a customer must publish to prove ownership. The token is shown once to the admin. */
export const domainVerificationInstructionsSchema = z.object({
    domainId: z.uuid(),
    recordType: z.literal('TXT'),
    recordName: z.string(),
    recordValue: z.string(),
});
export const DOMAIN_ACTIONS = ['verify', 'disable', 'enable', 'release'];
export const domainActionSchema = z.object({
    action: z.enum(DOMAIN_ACTIONS),
    reason: z.string().min(3).max(500),
});
/** 0.7.0: one custom host of an organization as Trace Admin manages it (released ones stay as history). */
export const adminDomainSchema = z.object({
    id: z.uuid(),
    organizationId: z.uuid(),
    hostname: z.string(),
    kind: domainKindSchema,
    applicationKey: productKeySchema,
    portalId: z.uuid().nullable(),
    verificationStatus: z.enum(DOMAIN_VERIFICATION_STATES),
    tlsStatus: z.enum(DOMAIN_TLS_STATES),
    lifecycle: z.enum(DOMAIN_LIFECYCLE_STATES),
    verifiedAt: z.iso.datetime().nullable(),
    releasedAt: z.iso.datetime().nullable(),
    /** A released host cannot be registered by another organization before this instant. */
    reassignableAfter: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
});
export const adminDomainListSchema = z.object({ items: z.array(adminDomainSchema) });
/** 0.7.0: the answer to POST domains: the record to publish (its token shown this once) and the new host. */
export const domainCreatedSchema = domainVerificationInstructionsSchema.extend({ domain: adminDomainSchema });
/* ------------------------------------------------------------------------------------------------ */
/* Support access and audit                                                                          */
/* ------------------------------------------------------------------------------------------------ */
/** Staff access to one organization's data beyond metadata: scoped, time limited, reasoned, audited. */
export const supportAccessRequestSchema = z.object({
    organizationId: z.uuid(),
    scope: z.enum(['read', 'write']),
    reason: z.string().min(10).max(1000),
    ticketRef: z.string().max(100).optional(),
    durationMinutes: z.number().int().min(15).max(480),
});
export const supportAccessGrantSchema = z.object({
    id: z.uuid(),
    organizationId: z.uuid(),
    staffUserId: z.uuid(),
    staffRole: staffRoleSchema,
    scope: z.enum(['read', 'write']),
    reason: z.string(),
    ticketRef: z.string().nullable(),
    startsAt: z.iso.datetime(),
    expiresAt: z.iso.datetime(),
    approvedBy: z.string().nullable(),
    revokedAt: z.iso.datetime().nullable(),
});
/** 0.7.0: GET support access grants: the caller's own (every staff member's for platform_admin). */
export const supportAccessListQuerySchema = z.object({
    organizationId: z.uuid().optional(),
    /** active (the default): grants in force now; all: every grant, newest first. */
    status: z.enum(['active', 'all']).default('active'),
});
export const supportAccessGrantListSchema = z.object({ items: z.array(supportAccessGrantSchema) });
export const auditEventViewSchema = z.object({
    id: z.uuid(),
    occurredAt: z.iso.datetime(),
    actor: z.object({ type: z.string(), id: z.string().nullable(), displayName: z.string().nullable() }),
    organizationId: z.uuid().nullable(),
    action: z.string(),
    target: z.object({ type: z.string(), id: z.string().nullable() }),
    reason: z.string().nullable(),
    /** Field-level changes, already redacted of anything secret. */
    changes: z.array(z.object({ field: z.string(), before: z.unknown(), after: z.unknown() })),
    correlationId: z.string().nullable(),
});
export const auditQuerySchema = z.object({
    organizationId: z.uuid().optional(),
    action: z.string().max(100).optional(),
    actorId: z.string().max(100).optional(),
    from: z.iso.datetime().optional(),
    to: z.iso.datetime().optional(),
    cursor: z.string().max(500).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
});
export const auditPageSchema = z.object({
    items: z.array(auditEventViewSchema),
    nextCursor: z.string().nullable(),
});
/* ------------------------------------------------------------------------------------------------ */
/* Side-effect authorization (internal API: service principals only)                                 */
/* ------------------------------------------------------------------------------------------------ */
/**
 * Asked by an acting service right before an external side effect (send, post, book, charge), and again by
 * a queued job right before it acts. Computed from all six layers at that moment; a failure to decide is
 * a deny.
 */
export const actionAuthorizeRequestSchema = z.object({
    organizationId: z.uuid(),
    moduleKey: z.string(),
    action: z.string().regex(/^[a-z_]+$/),
    actor: z.discriminatedUnion('type', [
        z.object({ type: z.literal('user'), userId: z.uuid() }),
        z.object({ type: z.literal('agent'), agentId: z.string(), onBehalfOfUserId: z.uuid().nullable() }),
        z.object({ type: z.literal('system'), job: z.string() }),
    ]),
});
export const actionAuthorizeResponseSchema = z.object({
    allowed: z.boolean(),
    state: moduleStateSchema,
    reasons: z.array(moduleReasonSchema),
    decidedAt: z.iso.datetime(),
});
/* ------------------------------------------------------------------------------------------------ */
/* Auth0 organization link (staff)                                                                   */
/* ------------------------------------------------------------------------------------------------ */
/**
 * POST an organization's Auth0 organization link (platform_admin, MFA): creates or adopts the Auth0 organization
 * of a Trace organization, sets its trace_org_id metadata (the trace-access-token-claims Action copies it into
 * product tokens), enables the configured sign-in connections on it and adds every active member whose Auth0
 * identity is linked. Idempotent: running it again repairs what drifted and adds members who joined since.
 * The body is the optional reason of reasonBodySchema.
 */
export const AUTH0_ORGANIZATION_SKIP_REASONS = ['no_auth0_identity'];
export const auth0OrganizationLinkSchema = z.object({
    /** The Auth0 organization id (org_...) stored on the Trace organization. */
    auth0OrganizationRef: z.string(),
    /** True when this run created the Auth0 organization (false: it existed, or was adopted by its metadata). */
    created: z.boolean(),
    /** The connections enabled on the Auth0 organization after this run (names). */
    connections: z.array(z.string()),
    membersAdded: z.number().int().nonnegative(),
    membersAlreadyPresent: z.number().int().nonnegative(),
    /** Active members who could not be added, and why (no Auth0 identity linked yet: they never signed in). */
    membersSkipped: z.array(z.object({ userId: z.uuid(), reason: z.enum(AUTH0_ORGANIZATION_SKIP_REASONS) })),
});
/**
 * The products an organization can subscribe to (Trace Admin, Products). Trace Admin itself is a product key for
 * sign-in, but staff reach it through their staff role, never through a subscription.
 */
export const SUBSCRIBABLE_PRODUCT_KEYS = ['enterprise', 'brokers', 'carriers', 'agents', 'portal'];
export const subscribableProductKeySchema = z.enum(SUBSCRIBABLE_PRODUCT_KEYS);
/**
 * The plan key of a subscription Trace staff add by hand (Trace Admin, Products) or the staff bootstrap adds: no
 * billing plan is attached. Billing contracts (Trace Admin, Billing) are separate and never read subscriptions.
 */
export const MANUAL_SUBSCRIPTION_PLAN_KEY = 'bootstrap';
export const PRODUCT_SUBSCRIPTION_STATUSES = ['trialing', 'active', 'past_due', 'canceled'];
/** One product subscription of an organization: live (trialing, active, past_due) or ended (canceled). */
export const adminProductSubscriptionSchema = z.object({
    id: z.uuid(),
    productKey: productKeySchema,
    planKey: z.string(),
    status: z.enum(PRODUCT_SUBSCRIPTION_STATUSES),
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
});
/** GET /v1/admin/organizations/{id}/subscriptions: every subscription, live first, then newest first. */
export const adminProductSubscriptionListSchema = z.object({
    items: z.array(adminProductSubscriptionSchema),
});
/**
 * POST /v1/admin/organizations/{id}/subscriptions: subscribe the organization to a product (plan key
 * MANUAL_SUBSCRIPTION_PLAN_KEY, status active). 409 subscription_exists when the product already has a live
 * subscription.
 */
export const productSubscriptionCreateRequestSchema = z.object({
    productKey: subscribableProductKeySchema,
    reason: z.string().trim().min(3).max(500),
});
/** POST /v1/admin/organizations/{id}/subscriptions/{subscriptionId}/end: the reason is required (audited). */
export const productSubscriptionEndRequestSchema = z.object({ reason: z.string().trim().min(3).max(500) });
/* ------------------------------------------------------------------------------------------------ */
/* Trace staff (platform admins only)                                                               */
/* ------------------------------------------------------------------------------------------------ */
export const STAFF_MEMBER_STATUSES = ['active', 'suspended', 'revoked'];
export const staffMemberStatusSchema = z.enum(STAFF_MEMBER_STATUSES);
/** One Trace staff member: the person, their staff role and status, and who granted it. */
export const staffMemberSchema = z.object({
    userId: z.uuid(),
    displayName: z.string(),
    email: z.string().nullable(),
    staffRole: staffRoleSchema,
    status: staffMemberStatusSchema,
    grantedAt: z.iso.datetime(),
    grantedByName: z.string().nullable(),
    expiresAt: z.iso.datetime().nullable(),
    /** The sign-in subjects linked to the person (for example google-oauth2|1234), so staff can tell accounts apart. */
    subjects: z.array(z.string()),
});
/** GET /v1/admin/staff: every staff member, active first, then by name. */
export const staffMemberListSchema = z.object({ items: z.array(staffMemberSchema) });
/**
 * POST /v1/admin/staff: make a person Trace staff now. Only for a person Trace already knows (they accepted an
 * invitation before): they are found by a linked sign-in's subject, or by the email of the one active person with it.
 * Never an identity created from an email. 404 person_not_found (invite their email instead: staffInvitations),
 * 409 staff_exists (already active staff; a suspended or revoked staff member is reactivated with the new role).
 */
export const staffMemberCreateRequestSchema = z
    .object({
    email: z.email().max(320).optional(),
    subject: z.string().trim().min(3).max(320).optional(),
    staffRole: staffRoleSchema,
    reason: z.string().trim().min(3).max(500),
})
    .refine((v) => (v.email === undefined) !== (v.subject === undefined), {
    message: 'Give either the email or the sign-in subject, not both.',
    path: ['email'],
});
/**
 * PATCH /v1/admin/staff/{userId}: change a staff member's role or status. 403 self_change for your own row;
 * 409 last_platform_admin when it would leave no active platform admin.
 */
export const staffMemberChangeSchema = z
    .object({
    staffRole: staffRoleSchema.optional(),
    status: staffMemberStatusSchema.optional(),
    reason: z.string().trim().min(3).max(500),
})
    .refine((v) => v.staffRole !== undefined || v.status !== undefined, {
    message: 'Change the role, the status, or both.',
    path: ['staffRole'],
});
export const STAFF_INVITATION_STATUSES = ['pending', 'accepted', 'revoked', 'expired'];
/**
 * An invitation to become Trace staff. A person who has never signed in to Trace has no record to grant a staff role
 * to, so a platform admin invites their email instead: accepting it at the hub, signed in with that verified address,
 * creates the person (or links the existing one) and grants the role. No organization is involved.
 */
export const staffInvitationSchema = z.object({
    id: z.uuid(),
    /** The invited address, masked (l***@trace.io): the full address is never returned. */
    emailHint: z.string(),
    staffRole: staffRoleSchema,
    status: z.enum(STAFF_INVITATION_STATUSES),
    createdAt: z.iso.datetime(),
    createdByName: z.string().nullable(),
    expiresAt: z.iso.datetime(),
    /** Development and test only (nothing is really delivered there): the accept link, to follow the flow locally. */
    acceptUrl: z.url().optional(),
});
/** GET /v1/admin/staff/invitations: the open staff invitations (pending, or expired and never accepted), newest first. */
export const staffInvitationListSchema = z.object({ items: z.array(staffInvitationSchema) });
/**
 * POST /v1/admin/staff/invitations: invite an email to become Trace staff with this role, delivered by email. Replaces
 * a pending invitation to the same address. 409 staff_exists when a person with that address is already active staff;
 * 409 self_change for one of your own addresses; 503 when no invitation delivery is configured.
 */
export const staffInvitationCreateRequestSchema = z.object({
    email: z.email().max(320),
    staffRole: staffRoleSchema,
    reason: z.string().trim().min(3).max(500),
    expiresInDays: z.number().int().min(1).max(30).default(7),
});
/** POST /v1/admin/staff/invitations/{invitationId}/revoke: withdraw a pending staff invitation (reason audited). */
export const staffInvitationRevokeRequestSchema = z.object({ reason: z.string().trim().min(3).max(500) });
/* ------------------------------------------------------------------------------------------------ */
/* Portals and their customer accounts (staff)                                                      */
/* ------------------------------------------------------------------------------------------------ */
export const PORTAL_STATUSES = ['active', 'disabled'];
export const CUSTOMER_ACCOUNT_STATUSES = ['active', 'suspended'];
/**
 * One customer portal an organization provides (trace-portal serves it; its id goes in trace-portal's PORTAL_IDS).
 * productKey is the provider's product the portal belongs to: the portal opens only while the provider has a live
 * subscription to it.
 */
export const adminPortalSchema = z.object({
    id: z.uuid(),
    productKey: productKeySchema,
    displayName: z.string(),
    status: z.enum(PORTAL_STATUSES),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const adminPortalListSchema = z.object({ items: z.array(adminPortalSchema) });
const portalDisplayName = z.string().trim().min(1).max(120);
const requiredReason = z.string().trim().min(3).max(500);
/** POST /v1/admin/organizations/{id}/portals: a new, active portal. */
export const portalCreateRequestSchema = z.object({
    productKey: subscribableProductKeySchema,
    displayName: portalDisplayName,
    reason: requiredReason,
});
/** PATCH /v1/admin/organizations/{id}/portals/{portalId}: rename, disable or enable. */
export const portalChangeSchema = z
    .object({
    displayName: portalDisplayName.optional(),
    status: z.enum(PORTAL_STATUSES).optional(),
    reason: requiredReason,
})
    .refine((v) => v.displayName !== undefined || v.status !== undefined, {
    message: 'Change the name, the status, or both.',
    path: ['displayName'],
});
/**
 * One customer account of a provider organization: a company that uses the provider's portals. externalRef is the
 * provider's own reference for it (for example the customer's id in its TMS), unique per provider when set.
 */
export const adminCustomerAccountSchema = z.object({
    id: z.uuid(),
    displayName: z.string(),
    externalRef: z.string().nullable(),
    status: z.enum(CUSTOMER_ACCOUNT_STATUSES),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const adminCustomerAccountListSchema = z.object({ items: z.array(adminCustomerAccountSchema) });
const externalRef = z.string().trim().min(1).max(200);
/** POST /v1/admin/organizations/{id}/customer-accounts. 409 customer_account_ref_exists for a reference in use. */
export const customerAccountCreateRequestSchema = z.object({
    displayName: portalDisplayName,
    externalRef: externalRef.nullish(),
    reason: requiredReason,
});
/** PATCH /v1/admin/organizations/{id}/customer-accounts/{accountId}: rename, change the reference, suspend. */
export const customerAccountChangeSchema = z
    .object({
    displayName: portalDisplayName.optional(),
    externalRef: externalRef.nullable().optional(),
    status: z.enum(CUSTOMER_ACCOUNT_STATUSES).optional(),
    reason: requiredReason,
})
    .refine((v) => v.displayName !== undefined || v.externalRef !== undefined || v.status !== undefined, {
    message: 'Change the name, the reference, the status, or several.',
    path: ['displayName'],
});
/* ------------------------------------------------------------------------------------------------ */
/* New organization (staff: platform admins), 0.23.0                                                */
/* ------------------------------------------------------------------------------------------------ */
/**
 * The slug rule of every organization (the database's organizations_slug_check): lowercase letters, digits and dashes,
 * 1 to 64 characters, starting and ending with a letter or digit.
 */
export const ORGANIZATION_SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/;
export const organizationSlugSchema = z
    .string()
    .regex(ORGANIZATION_SLUG_PATTERN, 'lowercase letters, digits and dashes, starting and ending with a letter or digit, 64 at most');
/**
 * The slug a company name suggests (self-serve organizations and Trace Admin's New organization): accents dropped,
 * lowercase letters and digits, runs of anything else as one dash, at most 56 characters so a "-2" suffix still fits
 * the 64 of the slug rule. A name with nothing usable suggests "organization".
 */
export function organizationSlugFor(name) {
    const base = name
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 56)
        .replace(/-+$/g, '');
    return base === '' ? 'organization' : base;
}
/** customer: a company Trace serves. internal: Trace's own (with no contract and never billed: billing exempt). */
export const ORGANIZATION_KINDS = ['customer', 'internal'];
export const organizationKindSchema = z.enum(ORGANIZATION_KINDS);
// Control characters (C0 and DEL) have no place in a name; Postgres refuses NUL outright.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARACTERS = /[\u0000-\u001f\u007f]/;
/**
 * POST /v1/admin/organizations (staff.orgs.manage: platform admins, MFA): creates the organization and, in one
 * request, everything a new customer needs to start: its Auth0 organization, its product subscriptions (plan key
 * MANUAL_SUBSCRIPTION_PLAN_KEY, as Trace Admin > Products adds them), an invitation of the owner as org_owner with
 * the chosen employee products, the billing exemption of an internal organization, and, with Enterprise, its
 * Enterprise provisioning (done once the owner accepts). No contract and no card: billing stays on the Billing tab.
 * 409 slug_taken for a slug in use; 409 email_domain_claimed when another organization claims the owner's email
 * domain and acknowledgeDomainClaim is not true (the detail names them; sending it again with true creates it).
 */
export const organizationCreateRequestSchema = z.object({
    displayName: z
        .string()
        .trim()
        .min(2)
        .max(120)
        .refine((v) => !CONTROL_CHARACTERS.test(v), 'must not contain control characters'),
    slug: organizationSlugSchema,
    kind: organizationKindSchema,
    products: z
        .array(subscribableProductKeySchema)
        .max(SUBSCRIBABLE_PRODUCT_KEYS.length)
        .refine((v) => new Set(v).size === v.length, 'name each product once'),
    ownerEmail: z.email().max(320),
    reason: z.string().trim().min(3).max(500),
    /** The second click of the dialog: create it even though another organization claims the owner's email domain. */
    acknowledgeDomainClaim: z.boolean().default(false),
});
/**
 * The Enterprise provisioning of an organization created in Trace Admin. Trace Enterprise creates its organization
 * with an existing Trace person as its first administrator, so it waits for the owner to accept their invitation
 * (waiting_for_owner), then runs (provisioning) and is retried after a failure (60 s, then 300 s) until it is complete,
 * or needs a staff retry after three failures in a row (needs_attention).
 */
export const ENTERPRISE_PROVISIONING_STATES = [
    'waiting_for_owner',
    'provisioning',
    'needs_attention',
    'complete',
];
export const enterpriseProvisioningStateSchema = z.enum(ENTERPRISE_PROVISIONING_STATES);
export const adminEnterpriseProvisioningSchema = z.object({
    id: z.uuid(),
    organizationId: z.uuid(),
    state: enterpriseProvisioningStateSchema,
    /** The owner who accepted, who becomes Enterprise's first administrator; null while waiting for them. */
    ownerUserId: z.uuid().nullable(),
    /** Failures in a row; a success sets it back to 0. */
    failureCount: z.number().int().nonnegative(),
    /** "<reason>: <message>" of the last failure (never a secret or Enterprise's own text), null when none. */
    lastError: z.string().nullable(),
    nextAttemptAt: z.iso.datetime().nullable(),
    /** Enterprise's own organization id, once it is created. */
    enterpriseOrganizationRef: z.string().nullable(),
    needsAttentionAt: z.iso.datetime().nullable(),
    completedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
/** GET /v1/admin/organizations/{id}/enterprise-provisioning (staff.orgs.read): null when there is none. */
export const adminEnterpriseProvisioningReadSchema = z.object({
    provisioning: adminEnterpriseProvisioningSchema.nullable(),
});
/**
 * POST /v1/admin/organizations/{id}/enterprise-provisioning/retry (staff.orgs.manage): a provisioning needing
 * attention runs again now, with a fresh failure count. Any other state: 409 enterprise_provisioning_not_retryable.
 */
export const enterpriseProvisioningRetryRequestSchema = z.object({ reason: requiredReason });
/**
 * Whether the new organization is linked to its Auth0 organization: linked; skipped (this environment has no Auth0
 * Management API); failed (Auth0 did not answer: the organization stands, and Link to Auth0 on its page repeats it).
 */
export const IDENTITY_PROVIDER_LINK_STATES = ['linked', 'skipped', 'failed'];
/** The answer to POST /v1/admin/organizations (201). */
export const organizationCreatedSchema = z.object({
    organization: adminOrganizationSummarySchema,
    kind: organizationKindSchema,
    billingExempt: z.boolean(),
    /** The owner's invitation (org_owner, the chosen employee products). */
    invitation: invitationSummarySchema,
    identityProvider: z.object({
        state: z.enum(IDENTITY_PROVIDER_LINK_STATES),
        auth0OrganizationRef: z.string().nullable(),
    }),
    /** The Enterprise provisioning, when Enterprise was chosen; null otherwise. */
    enterprise: adminEnterpriseProvisioningSchema.nullable(),
});
/** Milestone 2 route table (staff routes under the admin prefix; one internal route). */
export const PLATFORM_ADMIN_API = {
    modules: '/v1/admin/modules',
    organizationModules: (orgId) => `/v1/admin/organizations/${encodeURIComponent(orgId)}/modules`,
    organizationModule: (orgId, key) => `/v1/admin/organizations/${encodeURIComponent(orgId)}/modules/${encodeURIComponent(key)}`,
    organizationEntitlements: (orgId) => `/v1/admin/organizations/${encodeURIComponent(orgId)}/entitlements`,
    organizationEmergencyStops: (orgId) => `/v1/admin/organizations/${encodeURIComponent(orgId)}/emergency-stops`,
    releaseEmergencyStop: (orgId, stopId) => `/v1/admin/organizations/${encodeURIComponent(orgId)}/emergency-stops/${encodeURIComponent(stopId)}/release`,
    releaseFlags: '/v1/admin/release-flags',
    members: (orgId) => `/v1/admin/organizations/${encodeURIComponent(orgId)}/members`,
    member: (orgId, membershipId) => `/v1/admin/organizations/${encodeURIComponent(orgId)}/members/${encodeURIComponent(membershipId)}`,
    invitations: (orgId) => `/v1/admin/organizations/${encodeURIComponent(orgId)}/invitations`,
    revokeInvitation: (orgId, invitationId) => `/v1/admin/organizations/${encodeURIComponent(orgId)}/invitations/${encodeURIComponent(invitationId)}/revoke`,
    domains: (orgId) => `/v1/admin/organizations/${encodeURIComponent(orgId)}/domains`,
    domainAction: (orgId, domainId) => `/v1/admin/organizations/${encodeURIComponent(orgId)}/domains/${encodeURIComponent(domainId)}/actions`,
    organizationAuth0: (orgId) => `/v1/admin/organizations/${encodeURIComponent(orgId)}/auth0-organization`,
    subscriptions: (orgId) => `/v1/admin/organizations/${encodeURIComponent(orgId)}/subscriptions`,
    endSubscription: (orgId, subscriptionId) => `/v1/admin/organizations/${encodeURIComponent(orgId)}/subscriptions/${encodeURIComponent(subscriptionId)}/end`,
    staff: '/v1/admin/staff',
    staffMember: (userId) => `/v1/admin/staff/${encodeURIComponent(userId)}`,
    staffInvitations: '/v1/admin/staff/invitations',
    revokeStaffInvitation: (invitationId) => `/v1/admin/staff/invitations/${encodeURIComponent(invitationId)}/revoke`,
    portals: (orgId) => `/v1/admin/organizations/${encodeURIComponent(orgId)}/portals`,
    portal: (orgId, portalId) => `/v1/admin/organizations/${encodeURIComponent(orgId)}/portals/${encodeURIComponent(portalId)}`,
    portalInvitations: (orgId, portalId) => `/v1/admin/organizations/${encodeURIComponent(orgId)}/portals/${encodeURIComponent(portalId)}/invitations`,
    customerAccounts: (orgId) => `/v1/admin/organizations/${encodeURIComponent(orgId)}/customer-accounts`,
    customerAccount: (orgId, accountId) => `/v1/admin/organizations/${encodeURIComponent(orgId)}/customer-accounts/${encodeURIComponent(accountId)}`,
    /** 0.23.0: an organization's Enterprise provisioning (New organization) and its staff retry. */
    enterpriseProvisioning: (orgId) => `/v1/admin/organizations/${encodeURIComponent(orgId)}/enterprise-provisioning`,
    retryEnterpriseProvisioning: (orgId) => `/v1/admin/organizations/${encodeURIComponent(orgId)}/enterprise-provisioning/retry`,
    supportAccess: '/v1/admin/support-access',
    supportAccessRevoke: (grantId) => `/v1/admin/support-access/${encodeURIComponent(grantId)}/revoke`,
    audit: '/v1/admin/audit',
    /** 0.17.0: Trace Admin's Health page (health.ts), read from DigitalOcean. */
    health: '/v1/admin/health',
    /** 0.17.0: Trace Admin's Keys page (keys.ts): this environment's key names from Infisical, never a value. */
    keys: '/v1/admin/keys',
    keysSummary: '/v1/admin/keys/summary',
    keyExpiry: (project, name) => `/v1/admin/keys/${encodeURIComponent(project)}/${encodeURIComponent(name)}/expiry`,
    keyProvider: (project, name) => `/v1/admin/keys/${encodeURIComponent(project)}/${encodeURIComponent(name)}/provider`,
    keyTracking: (project, name) => `/v1/admin/keys/${encodeURIComponent(project)}/${encodeURIComponent(name)}/tracking`,
    /** 0.26.0: Trace Admin's Apps pages (platform-apps.ts): App Platform apps of this environment and their controls. */
    platformApps: '/v1/admin/apps',
    platformApp: (appId) => `/v1/admin/apps/${encodeURIComponent(appId)}`,
    platformAppDeployment: (appId, deploymentId) => `/v1/admin/apps/${encodeURIComponent(appId)}/deployments/${encodeURIComponent(deploymentId)}`,
    platformAppOperations: (appId) => `/v1/admin/apps/${encodeURIComponent(appId)}/operations`,
    actionAuthorize: '/v1/internal/actions/authorize',
};
//# sourceMappingURL=admin.js.map